import { useEffect, useRef, useState } from 'react'
import { api, upload } from '../api/client.ts'
import { SharePanel } from './SharePanel.tsx'
import { Icon } from './Icon.tsx'

/**
 * ЗАПИСЬ О ЗДОРОВЬЕ
 *
 * Заключение врача, результат анализов, снимок. Главное здесь — файл:
 * бумажку из поликлиники через год не найти, а сфотографированная она
 * лежит вместе с датой, врачом и тем, что было сказано.
 *
 * Дата записи — это дата приёма, а не дата загрузки. Их путают часто,
 * а разница в полгода превращает историю болезни в кашу.
 *
 * Делиться можно поштучно: «посмотри, что написал врач» не должно
 * открывать заодно список лекарств.
 */

type FileInfo = {
  id: string
  fileName: string
  mimeType: string
  url: string
  thumbUrl: string | null
}

export type RecordDraft = {
  id?: string
  kind: string
  title: string
  doctorName?: string | null
  clinic?: string | null
  specialty?: string | null
  recordedOn: string
  summary?: string | null
  eventId?: string | null
  files?: FileInfo[]
  mine?: boolean
}

export const RECORD_KINDS: Record<string, string> = {
  CONCLUSION: 'Заключение врача',
  LAB_RESULT: 'Анализы',
  PRESCRIPTION: 'Рецепт',
  VACCINATION: 'Прививка',
  IMAGING: 'Снимок, УЗИ, МРТ',
  OTHER: 'Другое',
}

type Appointment = { id: string; title: string; startsAt: string }

export function HealthRecordSheet({
  draft,
  onClose,
  onSaved,
}: {
  draft: RecordDraft
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)
  const readOnly = editing && draft.mine === false

  const [kind, setKind] = useState(draft.kind)
  const [title, setTitle] = useState(draft.title)
  const [doctorName, setDoctorName] = useState(draft.doctorName ?? '')
  const [clinic, setClinic] = useState(draft.clinic ?? '')
  const [recordedOn, setRecordedOn] = useState(draft.recordedOn.slice(0, 10))
  const [summary, setSummary] = useState(draft.summary ?? '')
  const [eventId, setEventId] = useState(draft.eventId ?? '')
  const [files, setFiles] = useState<FileInfo[]>(draft.files ?? [])
  const [appointments, setAppointments] = useState<Appointment[]>([])

  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sharing, setSharing] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  /**
   * Приёмы из планера за последний год — чтобы заключение можно было
   * прицепить к визиту, а не вспоминать потом, что это был за поход.
   */
  useEffect(() => {
    if (readOnly) return

    const to = new Date()
    to.setMonth(to.getMonth() + 1)
    const from = new Date()
    from.setMonth(from.getMonth() - 11)

    api<{ events: { id: string; title: string; startsAt: string; kind: string }[] }>(
      `/api/planner/agenda?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
    )
      .then((data) =>
        setAppointments(
          data.events
            .filter((e) => e.kind === 'APPOINTMENT')
            .map((e) => ({ id: e.id, title: e.title, startsAt: e.startsAt })),
        ),
      )
      .catch(() => setAppointments([]))
  }, [readOnly])

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const body = {
        kind,
        title: title.trim(),
        doctorName: doctorName.trim() || null,
        clinic: clinic.trim() || null,
        recordedOn,
        summary: summary.trim() || null,
        eventId: eventId || null,
      }

      if (draft.id) {
        await api(`/api/health/records/${draft.id}`, { method: 'PATCH', body })
      } else {
        const made = await api<{ record: { id: string } }>('/api/health/records', {
          method: 'POST',
          body,
        })
        // Файлы прикладываются к уже созданной записи, поэтому сначала
        // сохраняем её, а потом отдаём человеку возможность добавить снимки
        draft.id = made.record.id
      }

      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const addFile = async (file: File) => {
    if (!draft.id) {
      setError('Сначала сохрани запись — тогда к ней можно приложить файл')
      return
    }

    setBusy(true)
    setProgress(0)
    try {
      const form = new FormData()
      form.append('files', file, file.name)
      const result = await upload<{ attachments: FileInfo[] }>(
        `/api/attachments/HEALTH_RECORD/${draft.id}`,
        form,
        setProgress,
      )
      setFiles((current) => [...current, ...result.attachments])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Файл не загрузился')
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  const dropFile = async (id: string) => {
    setFiles((current) => current.filter((f) => f.id !== id))
    await api(`/api/attachments/${id}`, { method: 'DELETE' })
  }

  const remove = async () => {
    if (!draft.id) return
    if (!window.confirm('Удалить запись? Приложенные файлы тоже исчезнут.')) return

    setBusy(true)
    try {
      await api(`/api/health/records/${draft.id}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? title || 'Запись' : 'Новая запись'}</h2>
          {editing && !readOnly && (
            <button className="icon-btn" onClick={() => setSharing(true)} title="Кому показать">
              <Icon name="family" size={21} />
            </button>
          )}
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {readOnly ? (
          <>
            <p className="sheet-text">
              {RECORD_KINDS[kind]} · {new Date(recordedOn).toLocaleDateString('ru-RU')}
            </p>
            {doctorName && <p className="sheet-text">Врач: {doctorName}</p>}
            {clinic && <p className="sheet-text">{clinic}</p>}
            {summary && <p className="record-summary">{summary}</p>}
          </>
        ) : (
          <>
            <label className="field">
              <span className="field-label">Что это</span>
              <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
                {Object.entries(RECORD_KINDS).map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field-label">Название</span>
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Приём терапевта, общий анализ крови"
                maxLength={200}
                autoFocus={!editing}
              />
            </label>

            <div className="amount-row">
              <label className="field">
                <span className="field-label">Дата приёма</span>
                <input
                  className="input"
                  type="date"
                  value={recordedOn}
                  onChange={(e) => setRecordedOn(e.target.value)}
                />
              </label>

              <label className="field">
                <span className="field-label">Врач</span>
                <input
                  className="input"
                  value={doctorName}
                  onChange={(e) => setDoctorName(e.target.value)}
                  placeholder="Не обязательно"
                  maxLength={120}
                />
              </label>
            </div>

            <label className="field">
              <span className="field-label">Где</span>
              <input
                className="input"
                value={clinic}
                onChange={(e) => setClinic(e.target.value)}
                placeholder="Поликлиника, клиника"
                maxLength={120}
              />
            </label>

            {appointments.length > 0 && (
              <label className="field">
                <span className="field-label">К какому приёму в планере</span>
                <select
                  className="input"
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                >
                  <option value="">Ни к какому</option>
                  {appointments.map((visit) => (
                    <option key={visit.id} value={visit.id}>
                      {new Date(visit.startsAt).toLocaleDateString('ru-RU')} — {visit.title}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="field">
              <span className="field-label">Что сказали</span>
              <textarea
                className="input"
                rows={3}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Своими словами — через год это и пригодится"
                maxLength={4000}
              />
            </label>
          </>
        )}

        <h3 className="sheet-sub">Файлы</h3>
        <div className="record-files">
          {files.map((file) => (
            <div key={file.id} className="record-file">
              {file.thumbUrl ? (
                <a href={file.url} target="_blank" rel="noreferrer">
                  <img src={file.thumbUrl} alt="" />
                </a>
              ) : (
                <a className="record-file-doc" href={file.url} target="_blank" rel="noreferrer">
                  <span aria-hidden="true">📄</span>
                  <span className="record-file-name">{file.fileName}</span>
                </a>
              )}

              {!readOnly && (
                <button
                  className="item-photo-remove"
                  onClick={() => void dropFile(file.id)}
                  aria-label="Убрать файл"
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          {!readOnly && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void addFile(file)
                  e.target.value = ''
                }}
              />
              <button
                className="item-photo-add"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
              >
                {progress === null ? '+ файл' : `${progress}%`}
              </button>
            </>
          )}
        </div>

        {files.length === 0 && readOnly && <p className="sheet-note">Файлов нет</p>}

        {!readOnly && !draft.id && (
          <p className="sheet-note">
            Снимок заключения можно приложить сразу после сохранения записи.
          </p>
        )}

        {error && <div className="sheet-error">{error}</div>}

        {!readOnly && (
          <div className="sheet-actions">
            <button
              className="btn-primary"
              disabled={busy || !title.trim()}
              onClick={() => void save()}
            >
              {busy ? '…' : 'Сохранить'}
            </button>
            {editing && (
              <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
                Удалить
              </button>
            )}
          </div>
        )}

        {sharing && draft.id && (
          <SharePanel
            entityType="HEALTH_RECORD"
            entityId={draft.id}
            onClose={() => setSharing(false)}
            title="Кому показать эту запись"
          />
        )}
      </div>
    </div>
  )
}
